
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const CaseStudiesPage = () => {
  const caseStudies = [
    {
      id: 1,
      title: 'Sarah Johnson: From 12 to 50 Sales Per Year',
      subtitle: 'Luxury Real Estate Agent',
      results: {
        salesIncrease: '317%',
        timeReduction: '60%',
        leadQuality: '85%'
      },
      challenge: 'Sarah was struggling to manage her luxury property listings and generate quality leads in a competitive market.',
      solution: 'Implemented our AI staging tools, automated lead generation, and smart chatbot system.',
      outcome: 'Increased annual sales from 12 to 50 properties while reducing time spent on administrative tasks.',
      image: 'https://public.readdy.ai/ai/img_res/afa0c19646ee6aaff046e5bb8cf6840e.jpg',
      testimonial: 'The AI tools completely transformed my business. I can now focus on what I do best - building relationships with clients.',
      tools: ['AI Staging', 'Lead Generation', 'Smart Chatbots']
    },
    {
      id: 2,
      title: 'Mike Thompson: Market Domination Strategy',
      subtitle: 'Commercial Real Estate Broker',
      results: {
        salesIncrease: '425%',
        timeReduction: '70%',
        leadQuality: '92%'
      },
      challenge: 'Mike needed to scale his commercial real estate business but was overwhelmed by manual processes.',
      solution: 'Deployed comprehensive AI automation suite including market analytics and client management.',
      outcome: 'Became the top commercial broker in his region with 425% increase in closed deals.',
      image: 'https://readdy.ai/api/search-image?query=professional%20male%20real%20estate%20broker%20analyzing%20commercial%20property%20data%20on%20multiple%20screens%2C%20modern%20office%20environment%2C%20business%20success%20concept&width=600&height=400&seq=mike1&orientation=landscape',
      testimonial: 'The market analytics AI gives me insights my competitors don\'t have. It\'s like having a crystal ball.',
      tools: ['Market Analytics', 'Automation', 'Client Management']
    },
    {
      id: 3,
      title: 'Jennifer Lee: First-Time Agent Success',
      subtitle: 'New Real Estate Agent',
      results: {
        salesIncrease: '∞',
        timeReduction: '50%',
        leadQuality: '78%'
      },
      challenge: 'As a new agent, Jennifer lacked experience and struggled to compete with established professionals.',
      solution: 'Used our complete AI platform to level the playing field with automated tools and insights.',
      outcome: 'Closed 25 deals in her first year, outperforming many veteran agents.',
      image: 'https://readdy.ai/api/search-image?query=young%20professional%20female%20real%20estate%20agent%20using%20AI%20technology%20on%20tablet%2C%20confident%20business%20woman%20in%20modern%20office%2C%20success%20story%20concept&width=600&height=400&seq=jennifer1&orientation=landscape',
      testimonial: 'As a new agent, these AI tools gave me the confidence and capabilities to compete with veterans.',
      tools: ['AI Clone', 'Social Media AI', 'Training Platform']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              Success Stories
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Real results from real estate professionals who transformed their business with AI
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <div className="text-3xl font-bold">500%</div>
                <div className="text-blue-100">Average Sales Increase</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <div className="text-3xl font-bold">65%</div>
                <div className="text-blue-100">Time Savings</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <div className="text-3xl font-bold">90%</div>
                <div className="text-blue-100">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          {caseStudies.map((study, index) => (
            <div key={study.id} className={`mb-20 ${index !== caseStudies.length - 1 ? 'border-b border-gray-200 pb-20' : ''}`}>
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <img 
                    src={study.image} 
                    alt={study.title}
                    className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
                  />
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <h2 className="text-4xl font-bold mb-4 text-gray-800">{study.title}</h2>
                  <p className="text-xl text-blue-600 mb-6">{study.subtitle}</p>
                  
                  {/* Results */}
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{study.results.salesIncrease}</div>
                      <div className="text-sm text-gray-600">Sales Increase</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{study.results.timeReduction}</div>
                      <div className="text-sm text-gray-600">Time Reduction</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{study.results.leadQuality}</div>
                      <div className="text-sm text-gray-600">Lead Quality</div>
                    </div>
                  </div>

                  {/* Challenge, Solution, Outcome */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-bold text-red-600 mb-2">Challenge</h3>
                      <p className="text-gray-600">{study.challenge}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-blue-600 mb-2">Solution</h3>
                      <p className="text-gray-600">{study.solution}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-green-600 mb-2">Outcome</h3>
                      <p className="text-gray-600">{study.outcome}</p>
                    </div>
                  </div>

                  {/* Tools Used */}
                  <div className="mt-6">
                    <h3 className="text-lg font-bold mb-3">Tools Used:</h3>
                    <div className="flex flex-wrap gap-2">
                      {study.tools.map((tool, toolIndex) => (
                        <span key={toolIndex} className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm">
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Testimonial */}
                  <blockquote className="mt-8 p-6 bg-gray-50 rounded-lg border-l-4 border-blue-600">
                    <p className="text-gray-700 italic mb-4">"{study.testimonial}"</p>
                    <cite className="text-blue-600 font-medium">- {study.title.split(':')[0]}</cite>
                  </blockquote>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Write Your Success Story?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Join thousands of real estate professionals who have transformed their business with AI
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Start Free Trial
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudiesPage;
